import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { isInstalledStudentApp } from '@/lib/installedStudentApp';
import { consentNotice, initializeAdvertising, openCookieSettings, readCookieChoice, saveCookieChoice, shouldShowConsentBanner, subscribeConsent } from '@/lib/advertisingConsent';

export default function CookieConsent() {
  const { pathname } = useLocation();
  const homepage = pathname === '/' || pathname === '/bath';
  const privateOffer = pathname === '/instructor/private-offers' || pathname.startsWith('/offer/');
  const installedStudent = isInstalledStudentApp(pathname);
  const staffRoute = /^\/(?:admin|instructor)(?:\/|-|$)/.test(pathname);
  const learnerDashboard = pathname === '/student-home' || pathname === '/student-profile';
  const [, refresh] = useState(0);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const unsubscribe = subscribeConsent(() => refresh(value => value + 1));
    const open = () => {
      const choice = readCookieChoice();
      setAnalytics(choice?.analytics || false);
      setAdvertising(choice?.advertising || false);
      setManage(true);
    };
    window.addEventListener('bestdrive-open-cookie-settings', open);
    void initializeAdvertising();
    return () => { unsubscribe(); window.removeEventListener('bestdrive-open-cookie-settings', open); };
  }, []);
  const save = (analyticsChoice: boolean, adsChoice: boolean) => {
    try {
      saveCookieChoice(analyticsChoice, adsChoice);
      setError('');
      setManage(false);
    } catch { setError('Your browser could not save these choices. Optional tracking remains off; please allow local storage and try again.'); }
  };
  return <>
    {shouldShowConsentBanner() && !manage ? (
      <section aria-label="Cookie consent" className={`${privateOffer ? 'relative border-b p-4' : 'fixed bottom-0 inset-x-0 z-[10000] p-6'} border-t border-border bg-background text-foreground`}>
        <div className={`mx-auto max-w-5xl flex flex-col lg:flex-row ${privateOffer ? 'gap-3' : 'gap-6'} items-start lg:items-center`}>
          <div className="flex-1"><h2 className={`${privateOffer ? 'text-sm mb-1' : 'text-lg mb-2'} font-semibold`}>Your cookie choices</h2>
            {privateOffer ? <details className="text-xs leading-relaxed"><summary className="cursor-pointer">Essential cookies keep this page working. Optional tracking is your choice.</summary><p className="mt-2">{consentNotice.text} <a href="/privacy-policy" className="underline">Privacy policy</a></p></details> : <p className="text-sm">{consentNotice.text} <a href="/privacy-policy" className="underline">Privacy policy</a></p>}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button variant="outline" onClick={() => save(false, false)}>Reject</Button>
            <Button variant="outline" onClick={() => save(true, true)}>Accept</Button>
            <Button variant="outline" onClick={openCookieSettings}>Manage</Button>
          </div>
        </div>
      </section>
    ) : !manage && !installedStudent && !learnerDashboard && (staffRoute || privateOffer
      ? <div className="relative flex justify-center border-t border-border bg-background px-4 py-2"><Button variant="link" className="min-h-11 text-xs text-muted-foreground" onClick={openCookieSettings}>Cookie settings</Button></div>
      : <Button variant="outline" size="sm" className={`${privateOffer ? 'relative m-4' : `${homepage ? 'hidden md:inline-flex' : ''} fixed bottom-[calc(6rem+env(safe-area-inset-bottom,0px))] md:bottom-3 left-3 z-[9998]`} bg-background text-foreground`} onClick={openCookieSettings}>Cookie settings</Button>)}
    <Dialog open={manage} onOpenChange={setManage}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogTitle>Cookie settings</DialogTitle>
        <DialogDescription>{consentNotice.text}</DialogDescription>
        <p className="text-sm">Essential cookies: always on. No Google Analytics property is connected; accepting analytics does not install a new service.</p>
        <label className="flex items-center justify-between gap-4">Optional analytics<Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Optional analytics" className="data-[state=checked]:bg-primary" /></label>
        <label className="flex items-center justify-between gap-4">Google Ads measurement<Switch checked={advertising} onCheckedChange={setAdvertising} aria-label="Google Ads measurement" className="data-[state=checked]:bg-primary" /></label>
        <p className="text-sm">Browser privacy opt-outs are honoured even if you accept advertising. Enquiries work with either choice.</p>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => save(false, false)}>Reject</Button>
          <Button variant="outline" onClick={() => save(true, true)}>Accept</Button>
          <Button onClick={() => save(analytics, advertising)}>Save choices</Button>
        </div>
        <a href="/cookies-policy" className="underline text-sm">Cookies policy</a>
      </DialogContent>
    </Dialog>
  </>;
}